import { useState } from "react";
import { Dropdown } from "antd";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { ConnectEpicModal } from "./ConnectEpicModal";

// The app's own traditional navy/blue — the same color SidebarProfileCard's
// default avatarBg uses app-wide, reused here (not a one-off blue) so this
// button reads as a first-party MedSyncIQ action.
const PLATFORM_BLUE = "#180047";
// The app's traditional red, at its established 50%-over-white tint — the
// same flat value index.css's .msq-switch-dashboard-button documents as
// "#E4002B at 50% over white" — used only on the vendor row below, kept
// separate from PLATFORM_BLUE so each provider row keeps its own identity
// even though the trigger button above it is navy.
const EPIC_RED = "#F27F95";

/** One row per connectable data source, each styled in that platform's own
 *  hard brand color (not a soft app-tinted chip) — the same "keep the
 *  provider's own identity" reasoning a "Sign in with Google"-style button
 *  uses, so a patient can tell at a glance this leaves MedSyncIQ and talks
 *  to that hospital system directly. Add a new entry here (its own color +
 *  icon) when a second provider (e.g. athenahealth) goes live — nothing
 *  else about the menu needs to change. */
type ProviderOption = {
  id: string;
  label: string;
  color: string;
  icon: string;
};

const PROVIDERS: ProviderOption[] = [
  { id: "epic", label: "Connect with Epic", color: EPIC_RED, icon: "lucide:heart-pulse" },
];

export function ConnectDataButton() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showUnderDev, setShowUnderDev] = useState(false);

  const handleConnectClick = () => {
    setIsDropdownOpen(false);
    setIsModalOpen(true);
  };

  const menuContent = (
    <div className="w-[280px] overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-1.5 shadow-[0_4px_16px_rgba(17,24,39,0.14)]">
      {PROVIDERS.map((provider) => (
        <button
          key={provider.id}
          type="button"
          onClick={handleConnectClick}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left shadow-sm transition-transform hover:scale-[1.015] active:scale-[0.99]"
          style={{ background: provider.color }}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
            <Icon icon={provider.icon} className="h-4 w-4 text-white" />
          </span>
          <span className="text-sm font-bold text-white">{provider.label}</span>
        </button>
      ))}

      <div className="mx-3 my-1 h-px bg-[#F3F4F6]" />

      <div className="px-3 py-2">
        <p className="text-xs leading-snug text-[#9CA3AF]">More providers (athenahealth, others) coming soon</p>
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        trigger={["click"]}
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        popupRender={() => menuContent}
        placement="bottomRight"
      >
        <Button
          variant="outline"
          className="!flex !h-8 !items-center !gap-1.5 !rounded-full !border-none !px-3.5 !text-xs !font-bold !text-white hover:!opacity-90"
          style={{ background: PLATFORM_BLUE }}
        >
          <Icon icon="lucide:link-2" className="h-3.5 w-3.5 text-white" />
          Connect data
        </Button>
      </Dropdown>

      {/* Kept intact — rewire when Epic integration is ready */}
      <ConnectEpicModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Under development warning modal */}
      {showUnderDev && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40"
          onClick={() => setShowUnderDev(false)}
        >
          <div
            className="w-[340px] rounded-2xl border border-[#E5E7EB] bg-white px-7 py-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF7ED]">
              <Icon icon="lucide:construction" className="h-5 w-5 text-[#F59E0B]" />
            </div>
            <p className="text-sm font-extrabold text-[#111827]">Under Development</p>
            <p className="mt-1 text-sm text-[#6B7280]">
              This feature is currently under development and will be available soon. Stay tuned!
            </p>
            <Button
              variant="outline"
              onClick={() => setShowUnderDev(false)}
              className="!mt-5 !h-8 !w-full !rounded-full !border-[#E5E7EB] !text-xs !font-semibold !text-[#111827]"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
